import { Link } from 'react-router-dom';
import { walletView, type GetBillingOverviewResponse } from '@acs/shared';
import { Badge, Banner, Button, Card, Skeleton } from '@acs/ui';
import { useI18n } from '../../i18n/index.js';
import type { TranslationKey } from '../../i18n/index.js';
import { friendlyError } from '../../shared/api/errors.js';
import type { BusinessDoc } from '../../shared/api/converters.js';
import { AutoTopupWidget, useAutoTopup } from './AutoTopup.js';
import { CardSetupPending, useCardSetupReturn } from './useCardSetupReturn.js';
import { PrepaidActivation } from './Activation.js';
import { isSubscribed } from '../../shared/lib/subscription.js';
import { billingError, durationText } from './billingText.js';
import { formatMoney } from './format.js';
import { useOpenBillingPortal } from './hooks.js';
import { BillingFaq, BuyMinutes } from './MinutePlan.js';
import { useBillingHistory, useBillingOverview } from './prepaidHooks.js';

type Overview = GetBillingOverviewResponse;
/**
 * Settings › Billing for a PREPAID business (doc 42 §4): balance, the
 * subscription, buying minutes, automatic top-up, history, and the way to the
 * balance SMS settings — in that order.
 *
 * Every figure comes from the server. Nothing here paints a balance the server
 * has not confirmed: a payment in flight shows as "waiting for confirmation"
 * until the webhook (or a sync) has credited it.
 */
export function PrepaidBilling({ business, canceled = false, confirming = false }: { business: BusinessDoc; canceled?: boolean; confirming?: boolean }) {
  const { t } = useI18n();
  const overview = useBillingOverview(business);
  const setup = useCardSetupReturn(business.id);

  if (overview.isLoading) {
    return (
      <Card title={t('prepaid.balance.title')}>
        <Skeleton height={28} width="40%" />
        <div style={{ height: 12 }} />
        <Skeleton height={16} width="70%" />
      </Card>
    );
  }
  if (overview.isError || !overview.data) {
    return (
      <Banner
        tone="danger"
        title={t('prepaid.loadFailed')}
        actions={<Button variant="secondary" onClick={() => overview.refetch()}>{t('prepaid.retry')}</Button>}
      >
        {billingError(t, overview.error)}
      </Banner>
    );
  }
  const ov = overview.data;
  return (
    <>
      <CardSetupPending setup={setup} />
      <BalanceCard ov={ov} business={business} />
      <PlatformCard ov={ov} business={business} />
      {isSubscribed(business) ? (
        <>
          <TopupCard ov={ov} business={business} />
          <AutoTopupSection ov={ov} business={business} />
        </>
      ) : (
        // Not subscribed (lapsed or cancelled): the same activation widget
        // as the wizard. Minutes and auto top-up need a subscription, so
        // their cards wait until then. Hidden while a Checkout return is
        // confirming, so a paid owner is never offered a second subscription.
        !confirming && (
          <Card title={t('plan.title.activate')} id="billing-activate">
            <PrepaidActivation business={business} canceled={canceled} />
          </Card>
        )
      )}
      <HistoryCard businessId={business.id} currency={ov.currency} />
      <Card title={t('prepaid.sms.title')}>
        <p className="field-hint" style={{ margin: '0 0 12px' }}>{t('prepaid.sms.hint')}</p>
        <Link to={`/b/${business.id}/settings?section=messages#billing-sms`}>{t('prepaid.sms.link')}</Link>
      </Card>
    </>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="billing-row">
      <span className="billing-row__label">{label}</span>
      <span>{children}</span>
    </div>
  );
}

function BalanceCard({ ov, business }: { ov: Overview; business: BusinessDoc }) {
  const { t, tag } = useI18n();
  // Live figures from the business listener; the overview may be a refetch behind.
  const live = business.billingSummary;
  const view = walletView(live, new Date());
  const available = view.availableSeconds;
  const paymentState = live.paymentState;
  const debt = view.purchasedSeconds < 0 && view.includedRemainingSeconds === 0;
  const order = ov.activeOrder;
  const offerIncluded = (ov.included?.perPeriodSeconds ?? 0) > 0;
  const subscribed = business.subscription.status === 'active' || business.subscription.status === 'trialing';
  const preparing = offerIncluded && subscribed && !view.allowanceLive && !view.pendingAllowanceStart;
  // Just the figure and the way to top up; the problem banners below appear
  // only when something needs the owner.
  return (
    <Card
      title={t('prepaid.balance.title')}
      actions={
        <Button
          size="sm"
          onClick={() => (document.getElementById('billing-topup') ?? document.getElementById('billing-activate'))?.scrollIntoView({ behavior: 'smooth' })}
        >
          {t('prepaid.balance.topUp')}
        </Button>
      }
    >
      <div className="usage-head">
        <span className="usage-figure" style={debt ? { color: 'var(--tc-danger)' } : undefined}>
          {debt ? t('prepaid.balance.debt', { amount: durationText(t, -view.purchasedSeconds) }) : durationText(t, available)}
        </span>
        <span className="usage-remaining">{t('prepaid.balance.available')}</span>
      </div>
      {preparing && (
        <div style={{ marginTop: 12 }}>
          <Banner tone="info" title={t('plan.preparing.title')}>{t('plan.preparing.body')}</Banner>
        </div>
      )}

      {available <= 0 && (
        <div style={{ marginTop: 12 }}>
          <Banner tone="danger" title={t('prepaid.state.zero.title')}>{t('prepaid.state.zero.body')}</Banner>
        </div>
      )}
      {paymentState === 'pending' && order && (
        <div style={{ marginTop: 12 }}>
          <Banner tone="info" title={t('prepaid.state.pending.title')}>
            {t('prepaid.state.pending.body', { amount: formatMoney(order.grossMinor, ov.currency, tag) })}
          </Banner>
        </div>
      )}
      {paymentState === 'requires_action' && (
        <div style={{ marginTop: 12 }}>
          <Banner
            tone="warning"
            title={t('prepaid.state.action.title')}
            actions={
              order?.hostedInvoiceUrl ? (
                <a className="tc-btn tc-btn--primary tc-btn--sm" href={order.hostedInvoiceUrl} target="_blank" rel="noopener noreferrer">
                  {t('prepaid.state.action.confirm')}
                </a>
              ) : undefined
            }
          >
            {t('prepaid.state.action.body')}
          </Banner>
        </div>
      )}
      {paymentState === 'failed' && (
        <div style={{ marginTop: 12 }}>
          <Banner tone="danger" title={t('prepaid.state.failed.title')}>{t('prepaid.state.failed.body')}</Banner>
        </div>
      )}
      {paymentState === 'cap_reached' && (
        <div style={{ marginTop: 12 }}>
          <Banner tone="warning" title={t('prepaid.state.cap.title')}>{t('prepaid.state.cap.body')}</Banner>
        </div>
      )}
      {paymentState === 'price_review_required' && (
        <div style={{ marginTop: 12 }}>
          <Banner tone="warning" title={t('prepaid.state.price.title')}>{t('prepaid.state.price.body')}</Banner>
        </div>
      )}
    </Card>
  );
}

function PlatformCard({ ov, business }: { ov: Overview; business: BusinessDoc }) {
  const { t, tag } = useI18n();
  const portal = useOpenBillingPortal(business.id);
  const sub = business.subscription;
  const date = (d: Date | null) => (d ? new Intl.DateTimeFormat(tag, { dateStyle: 'long' }).format(d) : null);
  const subscribed = sub.status === 'active' || sub.status === 'trialing' || sub.status === 'past_due';
  // The offer is enrolled per business: only then does the fee include minutes.
  const includedMinutes = Math.floor((ov.included?.perPeriodSeconds ?? 0) / 60);
  const vat =
    ov.platform.taxBehavior === 'inclusive' ? t('prepaid.platform.vatIncluded') : ov.platform.taxBehavior === 'exclusive' ? t('prepaid.platform.vatExtra') : '';
  return (
    <Card title={t('prepaid.platform.title')}>
      <p className="field-hint" style={{ margin: '0 0 12px' }}>
        {includedMinutes > 0 ? t('prepaid.platform.hintIncluded', { minutes: includedMinutes }) : t('prepaid.platform.hint')}
      </p>
      {ov.entitlement.kind === 'grace' && ov.entitlement.deadline && (
        <div style={{ marginBottom: 12 }}>
          <Banner tone="warning" title={t('prepaid.platform.grace.title')}>
            {t('prepaid.platform.grace.body', { date: new Intl.DateTimeFormat(tag, { dateStyle: 'long', timeStyle: 'short' }).format(new Date(ov.entitlement.deadline)) })}
          </Banner>
        </div>
      )}
      {ov.entitlement.kind === 'blocked' && (
        <div style={{ marginBottom: 12 }}>
          <Banner tone="danger" title={t('prepaid.platform.blocked.title')}>
            {t(`prepaid.platform.blocked.${ov.entitlement.reason ?? 'no_subscription'}` as TranslationKey)}
          </Banner>
        </div>
      )}
      <Row label={t('settings.billing.status')}>
        <Badge tone={sub.status === 'active' || sub.status === 'trialing' ? 'success' : sub.status === 'past_due' ? 'warning' : 'neutral'}>
          {t(`settings.billing.status.${sub.status}` as TranslationKey)}
        </Badge>
      </Row>
      {ov.platform.unitAmountMinor !== null && (
        <Row label={t('prepaid.platform.price')}>
          {t('prepaid.platform.perMonth', { amount: formatMoney(ov.platform.unitAmountMinor, ov.currency, tag) })} {vat}
        </Row>
      )}
      {sub.currentPeriodEnd && (
        <Row label={sub.cancelAtPeriodEnd ? t('settings.billing.endsOn') : t('settings.billing.renewsOn')}>{date(sub.currentPeriodEnd)}</Row>
      )}
      {sub.cancelAtPeriodEnd && <p className="field-hint" style={{ marginTop: 8 }}>{t('prepaid.platform.cancelNote')}</p>}
      {portal.isError && (
        <div style={{ marginTop: 12 }}>
          <Banner tone="danger">{friendlyError(t, portal.error)}</Banner>
        </div>
      )}
      <div style={{ marginTop: 16 }}>
        {subscribed ? (
          <Button variant="secondary" loading={portal.isPending} onClick={() => portal.mutate()}>{t('settings.billing.manage')}</Button>
        ) : (
          // The activation widget below, not Checkout directly: it shows the
          // offer and the optional package (doc 57).
          <Button onClick={() => document.getElementById('billing-activate')?.scrollIntoView({ behavior: 'smooth' })}>{t('prepaid.platform.subscribe')}</Button>
        )}
      </div>
    </Card>
  );
}

/** Automatic top-up — the same widget as the wizard's Activate step. */
function AutoTopupSection({ ov, business }: { ov: Overview; business: BusinessDoc }) {
  const ctl = useAutoTopup(business, ov, { active: true, mode: 'settings' });
  return (
    <Card id="billing-auto">
      <div className="plan plan--embedded plan--card">
        <AutoTopupWidget ctl={ctl} business={business} subscribed />
      </div>
    </Card>
  );
}

/** Buying minutes — the same picker and summary as activation (doc 57). */
function TopupCard({ ov, business }: { ov: Overview; business: BusinessDoc }) {
  const { t } = useI18n();
  return (
    <Card title={t('prepaid.topup.title')} id="billing-topup">
      <div className="plan plan--embedded">
        <BuyMinutes business={business} ov={ov} />
        <BillingFaq includedMinutes={Math.floor((ov.included?.perPeriodSeconds ?? 0) / 60)} />
      </div>
    </Card>
  );
}

const HISTORY_TONE: Record<string, 'success' | 'warning' | 'danger' | 'neutral' | 'info'> = {
  paid: 'success',
  pending: 'info',
  requires_action: 'warning',
  failed: 'danger',
  void: 'neutral',
  refunded: 'neutral',
  partially_refunded: 'warning',
  review: 'warning',
};

function HistoryCard({ businessId, currency }: { businessId: string; currency: Overview['currency'] }) {
  const { t, tag } = useI18n();
  const history = useBillingHistory(businessId, true);
  const items = history.data?.pages.flatMap((p) => p.items) ?? [];
  return (
    <Card title={t('prepaid.history.title')}>
      {history.isLoading ? (
        <Skeleton height={16} width="60%" />
      ) : history.isError ? (
        <Banner tone="danger">{billingError(t, history.error)}</Banner>
      ) : items.length === 0 ? (
        <p className="field-hint">{t('prepaid.history.empty')}</p>
      ) : (
        <ul className="billing-history">
          {items.map((h) => (
            <li key={h.orderId} className="billing-history__row">
              <div>
                <div>
                  {new Intl.DateTimeFormat(tag, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(h.createdAt))}
                  {' · '}
                  {t(`prepaid.history.purpose.${h.purpose}` as TranslationKey)}
                </div>
                <div className="field-hint">
                  {/* A subscription row: the minutes its paid period includes
                      (none without the offer), then what it cost. */}
                  {h.purpose === 'subscription'
                    ? h.seconds > 0 && t('prepaid.history.included', { minutes: Math.floor(h.seconds / 60) })
                    : t('prepaid.history.minutes', { minutes: Math.floor(h.seconds / 60) })}
                  {h.purpose !== 'migration' &&
                    `${h.purpose === 'subscription' && h.seconds === 0 ? '' : ' · '}${formatMoney(h.grossMinor, h.currency ?? currency, tag)} (${t('prepaid.history.tax', { amount: formatMoney(h.taxMinor, h.currency ?? currency, tag) })})`}
                  {h.refundedSeconds > 0 && ` · ${t('prepaid.history.refunded', { minutes: Math.floor(h.refundedSeconds / 60) })}`}
                </div>
              </div>
              <div className="row" style={{ gap: 8, alignItems: 'center' }}>
                <Badge tone={HISTORY_TONE[h.status] ?? 'neutral'}>{t(`prepaid.history.status.${h.status}` as TranslationKey)}</Badge>
                {(h.receiptUrl ?? h.invoiceUrl) && (
                  <a href={(h.receiptUrl ?? h.invoiceUrl)!} target="_blank" rel="noopener noreferrer">{t('prepaid.history.invoice')}</a>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
      {history.hasNextPage && (
        <div style={{ marginTop: 12 }}>
          <Button variant="secondary" loading={history.isFetchingNextPage} onClick={() => history.fetchNextPage()}>{t('prepaid.history.more')}</Button>
        </div>
      )}
    </Card>
  );
}
